import { FC } from 'react'
import { PureQueryOptions } from '@apollo/client'
import {
  CoverLetterParagraphScrapListItem_CoverLetterParagraphScrapFragment,
  CoverLetterScrap_CurrentUserFragment,
  useCoverLetterParagraphScrapDeleteMutation,
  CoverLetterDetailPage_CoverLetterFragmentDoc,
  CoverLetterDetailPage_CoverLetterFragment,
} from 'generated/graphql'
import useErrorSnackbar from 'hooks/useErrorSnackbar'

interface ChildrenProps {
  loading: boolean
  deleteCoverLetterParagraphScrap: () => void
}

interface CoverLetterParagraphScrapDeleteProps {
  coverLetterParagraphScrap: CoverLetterParagraphScrapListItem_CoverLetterParagraphScrapFragment | null
  currentUser?: CoverLetterScrap_CurrentUserFragment | null
  refetchQueries?: PureQueryOptions[]
  children(state: ChildrenProps): void
}

const CoverLetterParagraphScrapDelete: FC<
  CoverLetterParagraphScrapDeleteProps
> = (props) => {
  const { currentUser, coverLetterParagraphScrap, children, refetchQueries } =
    props

  const { enqueueApolloError } = useErrorSnackbar()
  const [coverLetterParagraphScrapDelete, { loading }] =
    useCoverLetterParagraphScrapDeleteMutation({
      onError: enqueueApolloError,
      ...(refetchQueries && {
        refetchQueries,
        awaitRefetchQueries: true,
      }),
    })

  function deleteCoverLetterParagraphScrap() {
    if (!coverLetterParagraphScrap?.id) {
      return
    }

    if (!currentUser) {
      return
    }

    coverLetterParagraphScrapDelete({
      optimisticResponse: {
        __typename: 'Mutation',
        coverLetterParagraphScrapDelete: {
          __typename: 'CoverLetterParagraphScrapDeletePayload',
          userScrap: {
            __typename: 'UserScrap',
            id: 'optimisticResponse_ID',
            user: {
              __typename: 'User',
              id: currentUser.id,
              scrapCount: (currentUser?.scrapCount || 0) - 1,
            },
          },
          coverLetterParagraphScrap: {
            __typename: 'CoverLetterParagraphScrap',
            id: coverLetterParagraphScrap.id,
            paragraph: coverLetterParagraphScrap.paragraph,
          },
        },
      },
      variables: {
        id: coverLetterParagraphScrap.id,
      },
      update: (cache, { data }) => {
        const deletedCoverLetterParagraphScrapID =
          data?.coverLetterParagraphScrapDelete?.coverLetterParagraphScrap?.id

        if (deletedCoverLetterParagraphScrapID) {
          const coverLetter = coverLetterParagraphScrap?.coverLetter

          if (!coverLetter) {
            return
          }

          const cacheID = `CoverLetter:${coverLetter.id}`
          const fragmentName = 'CoverLetterDetailPage_coverLetter'

          const cachedCoverLetterFragmentData =
            cache.readFragment<CoverLetterDetailPage_CoverLetterFragment>({
              id: cacheID,
              fragmentName,
              fragment: CoverLetterDetailPage_CoverLetterFragmentDoc,
            })

          if (!cachedCoverLetterFragmentData) {
            return
          }

          const newScraps =
            cachedCoverLetterFragmentData.viewerParagraphScraps.filter(
              (scrap) => scrap.id !== deletedCoverLetterParagraphScrapID,
            )

          cache.writeFragment({
            id: cacheID,
            fragmentName,
            fragment: CoverLetterDetailPage_CoverLetterFragmentDoc,
            data: {
              ...cachedCoverLetterFragmentData,
              viewerParagraphScraps: newScraps,
            },
          })
        }
      },
    })
  }

  return <>{children({ loading, deleteCoverLetterParagraphScrap })}</>
}

export default CoverLetterParagraphScrapDelete
